// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import {action, makeObservable, observable} from 'mobx';

const BOTTOM_TOLERANCE = 2;

interface ChannelDimensions {
	channelId: string;
	scrollTop: number;
	scrollHeight: number;
	offsetHeight: number;
}

interface GuildDimensions {
	guildId: string;
	scrollTop: number | null;
	scrollTo: number | null;
}

interface GuildListDimensions {
	scrollTop: number;
}

function createDefaultGuildDimensions(guildId: string): GuildDimensions {
	return {
		guildId,
		scrollTop: null,
		scrollTo: null,
	};
}

class Dimension {
	channelDimensions = observable.map(new Map<string, ChannelDimensions>());
	guildDimensions = observable.map(new Map<string, GuildDimensions>());
	guildListDimensions = observable.box<GuildListDimensions>({scrollTop: 0}, {deep: false});

	constructor() {
		makeObservable(this, {
			recordChannelDimensions: action,
			updateGuildDimensions: action,
			updateGuildListDimensions: action,
			forgetChannelDimensions: action,
			reset: action,
		});
	}

	channelDimensionsFor(channelId: string): ChannelDimensions | undefined {
		return this.channelDimensions.get(channelId);
	}

	guildDimensionsFor(guildId: string): GuildDimensions {
		const existing = this.guildDimensions.get(guildId);
		if (existing != null) {
			return existing;
		}
		return createDefaultGuildDimensions(guildId);
	}

	currentGuildListDimensions(): GuildListDimensions {
		return this.guildListDimensions.get();
	}

	channelPinnedToEnd(channelId: string): boolean {
		const dimensions = this.channelDimensions.get(channelId);
		if (dimensions == null) {
			return true;
		}
		const {scrollTop, scrollHeight, offsetHeight} = dimensions;
		return scrollTop >= scrollHeight - offsetHeight - BOTTOM_TOLERANCE;
	}

	recordChannelDimensions(
		channelId: string,
		scrollTop: number | null,
		scrollHeight: number | null,
		offsetHeight: number | null,
		callback?: () => void,
	): void {
		const existing = this.channelDimensions.get(channelId);
		if (scrollTop == null || scrollHeight == null || offsetHeight == null) {
			if (existing != null) {
				this.channelDimensions.delete(channelId);
			}
			callback?.();
			return;
		}
		const newDimensions: ChannelDimensions = {
			channelId,
			scrollTop,
			scrollHeight,
			offsetHeight,
		};
		if (
			existing == null ||
			existing.scrollTop !== scrollTop ||
			existing.scrollHeight !== scrollHeight ||
			existing.offsetHeight !== offsetHeight
		) {
			this.channelDimensions.set(channelId, newDimensions);
		}
		callback?.();
	}

	updateGuildDimensions(guildId: string, scrollTop?: number | null, scrollTo?: number | null): void {
		let dimensions = this.guildDimensions.get(guildId);
		if (dimensions == null) {
			dimensions = createDefaultGuildDimensions(guildId);
		}
		const updated: GuildDimensions = {
			...dimensions,
			scrollTop: scrollTop !== undefined ? scrollTop : dimensions.scrollTop,
			scrollTo: scrollTo !== undefined ? scrollTo : dimensions.scrollTo,
		};
		this.guildDimensions.set(guildId, updated);
	}

	updateGuildListDimensions(scrollTop: number): void {
		if (this.guildListDimensions.get().scrollTop === scrollTop) {
			return;
		}
		this.guildListDimensions.set({scrollTop});
	}

	forgetChannelDimensions(channelId: string): void {
		this.channelDimensions.delete(channelId);
	}

	handleCallCreate(channelId: string): void {
		this.forgetChannelDimensions(channelId);
	}

	reset(): void {
		this.channelDimensions.clear();
		this.guildDimensions.clear();
		this.guildListDimensions.set({scrollTop: 0});
	}
}

const dimension = new Dimension();

ResettableStates.register(dimension, () => dimension.reset());

export default dimension;
